import React from "react";
import { Flex, theme } from "antd";
import { useTranslate } from "@refinedev/core";
import { TrashIcon } from "@phosphor-icons/react";
import { IconButton, Popconfirm, radius } from "@libertify/ui";
import { DocumentFile } from "../types";
import { FileIdentity } from "./FileIdentity";
import { PreviewButton } from "./PreviewButton";

interface DocumentEditRowProps {
  file: DocumentFile;
  onPreview: () => void;
  /** Omit to hide the delete action entirely (Source rows on edit — fixed,
   *  can't be removed). */
  onDelete?: () => void;
  deleting?: boolean;
}

/**
 * A single document row for the Edit Experience page's Documents card:
 * identity + Preview, and — Extras only — a confirm-guarded Delete. No
 * visibility toggle here: edit mode can't move a document between Source
 * files and Extras (see `DocumentsEditCard`).
 */
export const DocumentEditRow: React.FC<DocumentEditRowProps> = ({
  file,
  onPreview,
  onDelete,
  deleting,
}) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  return (
    <Flex
      align="center"
      gap={12}
      style={{
        padding: "10px 12px",
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: radius.box,
      }}
    >
      <FileIdentity file={file} />
      <PreviewButton onClick={onPreview} />
      {onDelete ? (
        <Popconfirm
          title={t("createExperience.addOns.documents.deleteConfirm")}
          onConfirm={onDelete}
          confirmText={t("buttons.delete")}
          cancelText={t("buttons.cancel")}
          danger
        >
          <IconButton
            variant="danger"
            size="s"
            aria-label={t("createExperience.addOns.documents.removeFile")}
            icon={<TrashIcon size={15} />}
            loading={deleting}
          />
        </Popconfirm>
      ) : null}
    </Flex>
  );
};
